/**
 * ImprovementPane — Read-only improvement intent display
 * 
 * Displays governed improvement intent, ratchet record, lineage summary, and next-step proposal.
 * This is a read-only inspector surface with no execution authority.
 * 
 * SAFETY: No execute/deploy/run/publish controls. Read-only display only.
 */

import React, { useState } from 'react';
import { GovernedImprovementPanel } from '../../modules/improvementIntentRouter/ui/GovernedImprovementPanel';
import { GovernedImprovementArtifactIndexPanel } from '../../modules/improvementIntentRouter/ui/GovernedImprovementArtifactIndexPanel';
import { GovernedImprovementCanonicalThreadDashboard } from '../../modules/improvementIntentRouter/ui/GovernedImprovementCanonicalThreadDashboard';
import { GovernedImprovementThreadStateTransitionPanel } from '../../modules/improvementIntentRouter/ui/GovernedImprovementThreadStateTransitionPanel';
import { GovernedImprovementResumeOrchestrationPanel } from '../../modules/improvementIntentRouter/ui/GovernedImprovementResumeOrchestrationPanel';
import { GovernedImprovementCanonicalThreadSummaryPanel } from '../../modules/improvementIntentRouter/ui/GovernedImprovementCanonicalThreadSummaryPanel';
import { GovernedImprovementBrowserVerifierStatic } from '../../modules/improvementIntentRouter/ui/GovernedImprovementBrowserVerifierStatic';
import { GovernedImprovementBrowserVerifierUploadPanel } from '../../modules/improvementIntentRouter/ui/GovernedImprovementBrowserVerifierUploadPanel';
import { GovernedImprovementSystemCanonicalRatchetIndexPanel } from '../../modules/improvementIntentRouter/ui/GovernedImprovementSystemCanonicalRatchetIndexPanel';
import { ImprovementIndexStatusBadge } from '../components/ImprovementIndexStatusBadge';
import {
  fixtureIntentOutput,
  fixtureRatchetRecord,
  fixtureLineageIndex,
  fixtureNextStepProposal,
  fixtureArtifactIndex,
  fixtureIndexStatusBadge,
  fixtureCanonicalThreads,
  fixtureThreadTransitions,
  fixtureResumeOrchestrations,
  fixtureCanonicalThreadSummaryExport,
  fixtureCanonicalRatchetIndex,
} from '../fixtures/improvementFixture';

type DisplayMode = 'summary' | 'detailed' | 'audit';

const STORAGE_KEY = 'trustgate:improvement-pane:display-mode';

function loadPersistedMode(): DisplayMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return 'summary';
    
    const mode = JSON.parse(stored);
    if (mode === 'summary' || mode === 'detailed' || mode === 'audit') {
      return mode;
    }
    return 'summary';
  } catch {
    return 'summary';
  }
}

function savePersistedMode(mode: DisplayMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(mode));
  } catch {
    // Silently fail - persistence is optional
  }
}

interface CollapsedSectionsState {
  currentImprovementState?: boolean;
  lineageAndArchive?: boolean;
  resumeTransitionSafety?: boolean;
  verificationAndReplay?: boolean;
}

interface PersistedCollapsedState {
  version: string;
  collapsedSections: CollapsedSectionsState;
}

const COLLAPSED_STATE_KEY = 'trustgate:improvement-pane:collapsed-sections';
const DEFAULT_COLLAPSED_STATE: CollapsedSectionsState = {
  currentImprovementState: false,
  lineageAndArchive: false,
  resumeTransitionSafety: false,
  verificationAndReplay: false,
};

function validateCollapsedState(data: unknown): CollapsedSectionsState | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;
  
  if (typeof obj.version !== 'string') return null;
  if (!obj.collapsedSections || typeof obj.collapsedSections !== 'object') return null;
  
  return obj.collapsedSections as CollapsedSectionsState;
}

function sanitizeCollapsedState(state: CollapsedSectionsState | null): CollapsedSectionsState {
  if (!state) return DEFAULT_COLLAPSED_STATE;

  const sanitized: CollapsedSectionsState = {
    currentImprovementState: typeof state.currentImprovementState === 'boolean' ? state.currentImprovementState : false,
    lineageAndArchive: typeof state.lineageAndArchive === 'boolean' ? state.lineageAndArchive : false,
    resumeTransitionSafety: typeof state.resumeTransitionSafety === 'boolean' ? state.resumeTransitionSafety : false,
    verificationAndReplay: typeof state.verificationAndReplay === 'boolean' ? state.verificationAndReplay : false,
  };

  return sanitized;
}

function loadCollapsedState(): CollapsedSectionsState {
  try {
    const stored = localStorage.getItem(COLLAPSED_STATE_KEY);
    if (!stored) return DEFAULT_COLLAPSED_STATE;

    const parsed = JSON.parse(stored) as PersistedCollapsedState;
    const validated = validateCollapsedState(parsed);
    const sanitized = sanitizeCollapsedState(validated);

    return sanitized;
  } catch {
    return DEFAULT_COLLAPSED_STATE;
  }
}

function saveCollapsedState(state: CollapsedSectionsState): void {
  try {
    const toPersist: PersistedCollapsedState = {
      version: '1.0',
      collapsedSections: state,
    };
    localStorage.setItem(COLLAPSED_STATE_KEY, JSON.stringify(toPersist));
  } catch {
    // Silently fail - persistence is optional
  }
}

export const ImprovementPane: React.FC = () => {
  const [displayMode, setDisplayMode] = useState<DisplayMode>(() => loadPersistedMode());
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    const loaded = loadCollapsedState();
    return {
      currentImprovementState: loaded.currentImprovementState ?? false,
      lineageAndArchive: loaded.lineageAndArchive ?? false,
      resumeTransitionSafety: loaded.resumeTransitionSafety ?? false,
      verificationAndReplay: loaded.verificationAndReplay ?? false,
    };
  });

  const toggleSection = (sectionKey: string) => {
    setCollapsedSections(prev => {
      const newState = {
        ...prev,
        [sectionKey]: !prev[sectionKey],
      };
      saveCollapsedState(newState);
      return newState;
    });
  };

  const SectionHeader: React.FC<{
    title: string;
    sectionKey: string;
    isCollapsed: boolean;
    onToggle: () => void;
    warningCount?: number;
    showExecutionAuthority?: boolean;
    showProofOnly?: boolean;
  }> = ({ title, sectionKey, isCollapsed, onToggle, warningCount = 0, showExecutionAuthority = false, showProofOnly = false }) => {
    const headerId = `${sectionKey}-header`;
    const contentId = `${sectionKey}-content`;
    
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px',
        paddingBottom: '6px',
        borderBottom: '1px solid #E5E7EB',
        flexWrap: 'wrap',
        gap: '8px',
      }} id={headerId}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '13px',
            fontWeight: 700,
            color: '#6B7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '6px',
          }}>
            {title}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            {warningCount > 0 && (
              <span style={{
                fontSize: '10px',
                fontWeight: 600,
                color: '#DC2626',
                backgroundColor: '#FEF2F2',
                padding: '2px 5px',
                borderRadius: '2px',
              }} role="status" aria-live="polite">
                {warningCount} warnings
              </span>
            )}
            {showExecutionAuthority && (
              <span style={{
                fontSize: '10px',
                fontWeight: 600,
                color: '#059669',
                backgroundColor: '#ECFDF5',
                padding: '2px 5px',
                borderRadius: '2px',
              }} role="status">
                executionAuthority: false
              </span>
            )}
            {showProofOnly && (
              <span style={{
                fontSize: '10px',
                fontWeight: 600,
                color: '#059669',
                backgroundColor: '#ECFDF5',
                padding: '2px 5px',
                borderRadius: '2px',
              }} role="status">
                proof-only / public-safe
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onToggle}
          style={{
            padding: '3px 6px',
            fontSize: '11px',
            fontWeight: 500,
            border: '1px solid #D1D5DB',
            backgroundColor: '#FFFFFF',
            color: '#374151',
            borderRadius: '3px',
            cursor: 'pointer',
            marginLeft: '10px',
          }}
          aria-label={isCollapsed ? `Expand ${title}` : `Collapse ${title}`}
          aria-expanded={!isCollapsed}
          aria-controls={contentId}
          type="button"
        >
          {isCollapsed ? '▼' : '▲'}
        </button>
      </div>
    );
  };

  const sectionHeaderStyle = {
    fontSize: '14px',
    fontWeight: 700,
    marginBottom: '12px',
    color: '#6B7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E5E7EB',
    paddingBottom: '6px',
    marginTop: '32px',
  };

  const firstSectionHeaderStyle = {
    fontSize: '14px',
    fontWeight: 700,
    marginBottom: '12px',
    color: '#6B7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    borderBottom: '1px solid #E5E7EB',
    paddingBottom: '6px',
    marginTop: '20px',
  };

  const summaryCardStyle = {
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '4px',
    padding: '10px',
    marginBottom: '10px',
    wordBreak: 'break-word' as const,
    overflowWrap: 'break-word' as const,
  };

  const summaryLabelStyle = {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6B7280',
    marginBottom: '3px',
  };

  const summaryValueStyle = {
    fontSize: '13px',
    color: '#111827',
    marginBottom: '6px',
    wordBreak: 'break-word' as const,
    overflowWrap: 'break-word' as const,
    whiteSpace: 'pre-wrap' as const,
  };

  const safetyLabelStyle = {
    fontSize: '10px',
    fontWeight: 600,
    color: '#059669',
    backgroundColor: '#ECFDF5',
    padding: '2px 5px',
    borderRadius: '2px',
    display: 'inline-block',
    marginRight: '3px',
    marginBottom: '3px',
    whiteSpace: 'nowrap' as const,
  };

  const auditHighlightStyle = {
    border: displayMode === 'audit' ? '2px solid #F59E0B' : '1px solid #E5E7EB',
    backgroundColor: displayMode === 'audit' ? '#FFFBEB' : '#F9FAFB',
    boxShadow: displayMode === 'audit' ? '0 0 0 1px #F59E0B' : 'none',
  };

  return (
    <div style={{
      height: '100%',
      overflow: 'auto',
      padding: 0,
    }}>
      {/* Mode Selector */}
      <div style={{
        marginBottom: '20px',
        padding: '12px',
        backgroundColor: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: '6px',
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: 600,
          marginBottom: '8px',
          color: '#374151',
        }}>
          Display Mode
        </div>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px',
          role: 'radiogroup',
          'aria-label': 'Display mode selector',
        }}>
          <button
            onClick={() => {
              setDisplayMode('summary');
              savePersistedMode('summary');
            }}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              border: displayMode === 'summary' ? '1px solid #3B82F6' : '1px solid #D1D5DB',
              backgroundColor: displayMode === 'summary' ? '#EBF5FF' : '#FFFFFF',
              color: '#374151',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            aria-label="Select Summary display mode"
            aria-pressed={displayMode === 'summary'}
            role="radio"
            type="button"
          >
            Summary
          </button>
          <button
            onClick={() => {
              setDisplayMode('detailed');
              savePersistedMode('detailed');
            }}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              border: displayMode === 'detailed' ? '1px solid #3B82F6' : '1px solid #D1D5DB',
              backgroundColor: displayMode === 'detailed' ? '#EBF5FF' : '#FFFFFF',
              color: '#374151',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            aria-label="Select Detailed display mode"
            aria-pressed={displayMode === 'detailed'}
            role="radio"
            type="button"
          >
            Detailed
          </button>
          <button
            onClick={() => {
              setDisplayMode('audit');
              savePersistedMode('audit');
            }}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              border: displayMode === 'audit' ? '1px solid #3B82F6' : '1px solid #D1D5DB',
              backgroundColor: displayMode === 'audit' ? '#EBF5FF' : '#FFFFFF',
              color: '#374151',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            aria-label="Select Audit display mode"
            aria-pressed={displayMode === 'audit'}
            role="radio"
            type="button"
          >
            Audit
          </button>
        </div>
      </div>

      {/* Current Improvement State */}
      <div role="region" aria-labelledby="current-improvement-state-header">
        <SectionHeader
          title="Current Improvement State"
          sectionKey="currentImprovementState"
          isCollapsed={collapsedSections.currentImprovementState}
          onToggle={() => toggleSection('currentImprovementState')}
          showExecutionAuthority={true}
        />
        {!collapsedSections.currentImprovementState && (
          <div id="current-improvement-state-content" role="region" aria-labelledby="current-improvement-state-header">
            {displayMode === 'summary' ? (
              <div style={{ ...summaryCardStyle, ...auditHighlightStyle }}>
                <div style={summaryLabelStyle}>Intent Class</div>
                <div style={summaryValueStyle}>{fixtureIntentOutput.intentClass}</div>
                <div style={summaryLabelStyle}>Ratchet Status</div>
                <div style={summaryValueStyle}>{fixtureRatchetRecord.status}</div>
                <div style={summaryLabelStyle}>Recommended Ratchet</div>
                <div style={summaryValueStyle}>{fixtureIntentOutput.recommendedRatchet}</div>
                <div style={{ marginTop: '8px' }}>
                  <span style={safetyLabelStyle}>Read-only</span>
                  <span style={safetyLabelStyle}>No execution authority</span>
                </div>
              </div>
            ) : (
              <>
                <GovernedImprovementPanel
                  intentOutput={fixtureIntentOutput}
                  ratchetRecord={fixtureRatchetRecord}
                  lineageIndex={fixtureLineageIndex}
                  nextStepProposal={fixtureNextStepProposal}
                />
                <div style={{ marginTop: '24px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <ImprovementIndexStatusBadge badgeData={fixtureIndexStatusBadge} />
                  </div>
                  <GovernedImprovementArtifactIndexPanel
                    indexData={fixtureArtifactIndex}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Lineage & Archive */}
      <div role="region" aria-labelledby="lineage-and-archive-header">
        <SectionHeader
          title="Lineage & Archive"
          sectionKey="lineageAndArchive"
          isCollapsed={collapsedSections.lineageAndArchive}
          onToggle={() => toggleSection('lineageAndArchive')}
          showProofOnly={true}
        />
        {!collapsedSections.lineageAndArchive && (
          <div id="lineage-and-archive-content" role="region" aria-labelledby="lineage-and-archive-header">
            {displayMode === 'summary' ? (
              <div style={{ ...summaryCardStyle, ...auditHighlightStyle }}>
                <div style={summaryLabelStyle}>Canonical Ratchets</div>
                <div style={summaryValueStyle}>{fixtureCanonicalRatchetIndex.canonicalRatchets}</div>
                <div style={summaryLabelStyle}>Archived Status</div>
                <div style={summaryValueStyle}>{fixtureCanonicalRatchetIndex.latestCompletedArchive.archivedStatus}</div>
                <div style={summaryLabelStyle}>Archive Index Hash</div>
                <div style={summaryValueStyle}>{fixtureCanonicalRatchetIndex.indexHash.substring(0, 16)}...</div>
                <div style={{ marginTop: '8px' }}>
                  <span style={safetyLabelStyle}>Archived lineage immutable</span>
                  <span style={safetyLabelStyle}>No execution authority</span>
                </div>
              </div>
            ) : (
              <>
                <GovernedImprovementSystemCanonicalRatchetIndexPanel
                  ratchetIndex={fixtureCanonicalRatchetIndex}
                />
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    marginBottom: '12px',
                    color: '#374151',
                  }}>
                    Canonical Thread Dashboard
                  </h3>
                  <GovernedImprovementCanonicalThreadDashboard
                    canonicalThreads={fixtureCanonicalThreads}
                  />
                </div>
                <div style={{ marginTop: '32px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    marginBottom: '12px',
                    color: '#374151',
                  }}>
                    Canonical Thread Summary Export
                  </h3>
                  <GovernedImprovementCanonicalThreadSummaryPanel
                    summary={fixtureCanonicalThreadSummaryExport}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Resume / Transition Safety */}
      <div role="region" aria-labelledby="resume-transition-safety-header">
        <SectionHeader
          title="Resume / Transition Safety"
          sectionKey="resumeTransitionSafety"
          isCollapsed={collapsedSections.resumeTransitionSafety}
          onToggle={() => toggleSection('resumeTransitionSafety')}
          showExecutionAuthority={true}
        />
        {!collapsedSections.resumeTransitionSafety && (
          <div id="resume-transition-safety-content" role="region" aria-labelledby="resume-transition-safety-header">
            {displayMode === 'summary' ? (
              <div style={{ ...summaryCardStyle, ...auditHighlightStyle }}>
                <div style={summaryLabelStyle}>Thread Transitions</div>
                <div style={summaryValueStyle}>{fixtureThreadTransitions.length} transitions</div>
                <div style={summaryLabelStyle}>Resume Orchestrations</div>
                <div style={summaryValueStyle}>{fixtureResumeOrchestrations.length} orchestrations</div>
                <div style={{ marginTop: '8px' }}>
                  <span style={safetyLabelStyle}>Read-only</span>
                  <span style={safetyLabelStyle}>No execution authority</span>
                </div>
              </div>
            ) : (
              <>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  marginBottom: '12px',
                  color: '#374151',
                }}>
                  Thread State Transitions
                </h3>
                {fixtureThreadTransitions.map((transition, index) => (
                  <div key={index} style={{ marginBottom: '16px' }}>
                    <GovernedImprovementThreadStateTransitionPanel
                      transition={transition}
                    />
                  </div>
                ))}
                <div style={{ marginTop: '24px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    marginBottom: '12px',
                    color: '#374151',
                  }}>
                    Resume Orchestration
                  </h3>
                  {fixtureResumeOrchestrations.map((orchestration, index) => (
                    <div key={index} style={{ marginBottom: '16px' }}>
                      <GovernedImprovementResumeOrchestrationPanel
                        orchestration={orchestration}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Verification & Replay */}
      <div role="region" aria-labelledby="verification-and-replay-header">
        <SectionHeader
          title="Verification & Replay"
          sectionKey="verificationAndReplay"
          isCollapsed={collapsedSections.verificationAndReplay}
          onToggle={() => toggleSection('verificationAndReplay')}
          showProofOnly={true}
        />
        {!collapsedSections.verificationAndReplay && (
          <div id="verification-and-replay-content" role="region" aria-labelledby="verification-and-replay-header">
            {displayMode === 'summary' ? (
              <div style={{ ...summaryCardStyle, ...auditHighlightStyle }}>
                <div style={summaryLabelStyle}>Client-Side Verifier</div>
                <div style={summaryValueStyle}>Available</div>
                <div style={summaryLabelStyle}>Upload Verifier</div>
                <div style={summaryValueStyle}>Available</div>
                <div style={{ marginTop: '8px' }}>
                  <span style={safetyLabelStyle}>Proof-only</span>
                  <span style={safetyLabelStyle}>Public-safe</span>
                  <span style={safetyLabelStyle}>No execution authority</span>
                </div>
              </div>
            ) : (
              <>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  marginBottom: '12px',
                  color: '#374151',
                }}>
                  Client-Side Public Summary Verification
                </h3>
                <GovernedImprovementBrowserVerifierStatic />
                <div style={{ marginTop: '24px' }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    marginBottom: '12px',
                    color: '#374151',
                  }}>
                    Upload Verifier
                  </h3>
                  <GovernedImprovementBrowserVerifierUploadPanel />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
